import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { getHandicap } from '../utils/Utilities';
import { UpdateUser, AddUser, getUserMin } from '../isdlData/matrixUserData';
import { Cross2Icon } from '../icons/Icons';

const UserForm = forwardRef(({ User, FinishedChanging }, ref) => {

    const [isEditing, setIsEditing] = useState(true);
    const [validUser, setValidUser] = useState(false);
    const [externalUser, setExternalUser] = useState(false);

    const emailInput = useRef(null);
    const firstNameInput = useRef(null);
    const lastNameInput = useRef(null);
    const handicapInput = useRef(null);
    const handednessRightRadio = useRef(null);
    const handednessLeftRadio = useRef(null);
    const genderMaleRadio = useRef(null);
    const genderFemaleRadio = useRef(null);
    const handleEnterToNext = (event, nextRef) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();

        if (nextRef?.current) {
            nextRef.current.focus();
        }
    };

    useEffect(() => {
        if(User){//editing
            const userData = User.data;
            setIsEditing(true);
            setExternalUser(false);
            emailInput.current.value = userData.id;
            firstNameInput.current.value = userData.FirstName;
            lastNameInput.current.value = userData.LastName;
            handicapInput.current.value = getHandicap(userData.Handicap);
            handednessRightRadio.current.checked = userData.Handedness.toLowerCase() === "right";
            handednessLeftRadio.current.checked = userData.Handedness.toLowerCase() === "left";
            genderMaleRadio.current.checked = userData.Gender.toLowerCase() === "male";
            genderFemaleRadio.current.checked = userData.Gender.toLowerCase() === "female";
            handleValidation(false);

        }else{//adding
            setIsEditing(false);
            emailInput.current.value = "";
            firstNameInput.current.value = "";
            lastNameInput.current.value = "";
            handicapInput.current.value = "";
            handednessRightRadio.current.checked = true;
            handednessLeftRadio.current.checked = false;
            genderMaleRadio.current.checked = true;
            genderFemaleRadio.current.checked = false;
        }

        return () => {
            //cleanup here
        };
    },[User]);

    const handleValidation = async () => {
        if(externalUser){
            if (!firstNameInput.current.value || !lastNameInput.current.value) {
                firstNameInput.current.style.outline = "2px solid red";
                lastNameInput.current.style.outline = "2px solid red";
                setValidUser(false);
            } else {
                firstNameInput.current.style.outline = "";
                lastNameInput.current.style.outline = "";
                const potentialEmail = getExternalEmail(firstNameInput.current.value, lastNameInput.current.value);
                const validEmail = await validateEmail(potentialEmail, true);
                if (!validEmail) {//email invalid
                    firstNameInput.current.style.outline = "2px solid red";
                    lastNameInput.current.style.outline = "2px solid red";
                    setValidUser(false);
                    return;
                } else {
                    firstNameInput.current.style.outline = "";
                    lastNameInput.current.style.outline = "";
                }
            }
        }else{
            const validEmail = await validateEmail(emailInput.current.value.toLowerCase(), !isEditing);
            if (!validEmail) {//email invalid
                emailInput.current.style.outline = "2px solid red";
                setValidUser(false);
                return;
            } else {
                emailInput.current.style.outline = "";
            }
        }

        if(!firstNameInput.current.value){//first name invalid
            firstNameInput.current.style.outline = "2px solid red";
            setValidUser(false);
            return;
        }else{
            firstNameInput.current.style.outline = "";
        }

        if(!lastNameInput.current.value){//last name invalid
            lastNameInput.current.style.outline = "2px solid red";
            setValidUser(false);
            return;
        }else{
            lastNameInput.current.style.outline = "";
        }

        if(!validateHandicap(handicapInput.current.value)){
            handicapInput.current.style.outline = "2px solid red";
            setValidUser(false);
            return;
        }else{
            handicapInput.current.style.outline = "";
        }

        if(!handednessRightRadio.current.checked && !handednessLeftRadio.current.checked ||//invalid handedness
            handednessRightRadio.current.checked && handednessLeftRadio.current.checked){
                handednessRightRadio.current.style.outline = "2px solid red";
                handednessLeftRadio.current.style.outline = "2px solid red";
                setValidUser(false);
                return;
        }else{
            handednessRightRadio.current.style.outline = "";
            handednessLeftRadio.current.style.outline = "";
        }

        if(!genderMaleRadio.current.checked && !genderFemaleRadio.current.checked ||//invalid gender
            genderMaleRadio.current.checked && genderFemaleRadio.current.checked){
                genderMaleRadio.current.style.outline = "2px solid red";
                genderFemaleRadio.current.style.outline = "2px solid red";
                setValidUser(false);
            return;
        }else{
            genderMaleRadio.current.style.outline = "";
            genderFemaleRadio.current.style.outline = "";
        }

        setValidUser(true);
    }

    async function validateEmail(email, isNew) {
        const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
        if(emailRegex.test(email)){
            let existingUser = null;
            try{
                existingUser = await getUserMin(email);
            }catch(e){
                console.error(e.message);
            }
            if(isNew) {
                if(existingUser === null || existingUser.length === 0){
                    return true;
                }else{
                    return false;
                }
            }else{
                return true;
            }
            
        }else{
            return false;
        }
    }

    function validateHandicap(entry) {
        let isValid = false;
    
        // Check if entry is null or empty
        if (entry === null || entry === '') {
            return false;
        }
    
        // Regular expression to validate a floating-point number
        const numberRegex = /^[-+]?\d*\.?\d+$/;
    
        // Check if the entry is a valid number
        const isNumber = numberRegex.test(entry);
    
        if (!isNumber && entry.toLowerCase() === 'nh') {
            isValid = true;
        } else if (!isNumber && entry[0] === '+') {
            const innerNumber = entry.substring(1);
    
            if (numberRegex.test(innerNumber)) {
                isValid = true;
            }
        } else if (isNumber) {
            isValid = true;
        }
    
        return isValid;
    }

    function convertHandicapToDBFriendly(handicap) {
        if (validateHandicap(handicap)) {
            if (handicap.toLowerCase() === "nh") {
                return -9999;
            } else if (handicap[0] === '+') {
                const innerNumber = parseFloat(handicap.substring(1));
                return innerNumber;
            } else {
                const alreadyNegative = handicap[0] === '-';
                return parseFloat(handicap) * (alreadyNegative ? 1 : -1);
            }
        } else {
            return -9999;
        }
    }

    const closeModal = () => {
        FinishedChanging(null);
    }

    const finishUserForm = async () => {
        if(isEditing){
            let userData = User.data;
            userData.id = emailInput.current.value.toLowerCase();
            userData.Email = emailInput.current.value.toLowerCase();
            userData.FirstName = firstNameInput.current.value;
            userData.LastName = lastNameInput.current.value;
            userData.FullName = userData.FirstName + " " + userData.LastName;
            userData.Handicap = convertHandicapToDBFriendly(handicapInput.current.value);
            userData.Handedness = handednessRightRadio.current.checked ? "Right" : "Left";
            userData.Gender = genderMaleRadio.current.checked ? "Male" : "Female";
            // userData.Role = "Admin";
            await UpdateUser(userData);
            FinishedChanging(userData);
            //update
        }
        else{
            let email = externalUser ? getExternalEmail(firstNameInput.current.value, lastNameInput.current.value) : emailInput.current.value.toLowerCase();

            let NewUser = {};
            NewUser["id"] = email;
            NewUser["Email"] = email;
            NewUser["FirstName"] = firstNameInput.current.value;
            NewUser["LastName"] = lastNameInput.current.value;
            NewUser["FullName"] = firstNameInput.current.value + " " + lastNameInput.current.value;
            NewUser["Handicap"] = convertHandicapToDBFriendly(handicapInput.current.value);
            NewUser["Handedness"] = handednessRightRadio.current.checked ? "Right" : "Left";
            NewUser["Gender"] = genderMaleRadio.current.checked ? "Male" : "Female";
            NewUser["Address1"] = "";
            NewUser["Address2"] = "";
            NewUser["Phone"] = "";
            NewUser["Role"] = "User";
            //create
            await AddUser(NewUser);
            FinishedChanging(NewUser);
        }
    }

    function getExternalEmail(firstName, lastName){
        return firstName.toLowerCase() + "." + lastName.toLowerCase() + "@callawayextuser.com";
    }

    return (
        <div style={{display:'flex', flexDirection:'column'}} className='user-form-container' ref={ref}>
            <button className='modal-close-button' onClick={closeModal}><Cross2Icon width='30px' height='30px'/></button>
            <div className='modal-header'>User</div>
            {!isEditing && (
                <div style={{ display: 'flex', flexDirection: 'row' }} className='user-form-item-container'>
                    <label htmlFor='extUser' className='user-form-label'>External User</label>
                    <input id='extUser' type='checkbox' className='user-form-input' style={{width:'30px', height:'30px'}} onChange={(e) => setExternalUser(e.target.checked)}/>
                </div>
            )}
            {!externalUser && (
                <div style={{ display: 'flex', flexDirection: 'row' }} className='user-form-item-container'>
                    <label htmlFor='emailInput' className='user-form-label'>Email</label>
                    <input id='emailInput' type='email' className='user-form-input' disabled={isEditing} ref={emailInput} onKeyUp={handleValidation} style={{ textTransform: 'lowercase' }} />
                </div>
            )}
            <div style={{display:'flex', flexDirection:'row'}} className='user-form-item-container'>
                <label htmlFor='firstName' className='user-form-label'>First Name</label>
                <input id='firstName' type='text' className='user-form-input' ref={firstNameInput} onKeyUp={handleValidation}/>
            </div>
            <div style={{display:'flex', flexDirection:'row'}} className='user-form-item-container'>
                <label htmlFor='lastName' className='user-form-label'>Last Name</label>
                <input id='lastName' type='text' className='user-form-input' ref={lastNameInput} onKeyUp={handleValidation}/>
            </div>
            <div style={{display:'flex', flexDirection:'row'}} className='user-form-item-container'>
                <label htmlFor='handicap' className='user-form-label'>Handicap</label>
                <input id='handicap' type='text' className='user-form-input' ref={handicapInput} onKeyUp={handleValidation}/>
            </div>
            <div style={{display:'flex', flexDirection:'row'}} className='user-form-item-container'>
                <label className='user-form-label'>Handedness</label>
                <input id='right' type='radio' name='handedness' ref={handednessRightRadio} style={{width:'20px', height:'20px', transform:'translateY(8px)'}}/>
                <label htmlFor='right' className='user-form-radio-label'>Right</label>
                <input id='left' type='radio' name='handedness' ref={handednessLeftRadio} style={{width:'20px', height:'20px', transform:'translateY(8px)'}}/>
                <label htmlFor='left' className='user-form-radio-label'>Left</label>
            </div>
            <div style={{display:'flex', flexDirection:'row'}} className='user-form-item-container'>
                <label className='user-form-label'>Gender</label>
                <input id='male' type='radio' name='gender' ref={genderMaleRadio} style={{width:'20px', height:'20px', transform:'translateY(8px)'}}/>
                <label htmlFor='male' className='user-form-radio-label'>Male</label>
                <input id='female' type='radio' name='gender' ref={genderFemaleRadio} style={{width:'20px', height:'20px', transform:'translateY(8px)'}}/>
                <label htmlFor='female' className='user-form-radio-label'>Female</label>
            </div>
            <div style={{marginBottom:'10px', display:'flex', flexDirection:'row', justifyContent: 'space-evenly', width:'100%'}}>
                {/* <button className='button' style={{width:'100px'}} onClick={closeModal}>Close</button> */}
                <button className='button' style={{width:'100px'}} disabled={!validUser} onClick={finishUserForm}>{isEditing ? "Update" : "Create"}</button>
            </div>
        </div>
    );
});

export default UserForm;